import {
  Card, DataTable, Badge, Button, TextField, InlineStack, BlockStack,
  Spinner, Text, Box, Pagination, Banner,
} from '@shopify/polaris';
import type { AdminShop } from '../../api/admin';

interface Props {
  shops: AdminShop[];
  loading: boolean;
  error: string | null;
  actionId: number | null;
  page: number;
  lastPage: number;
  total: number;
  search: string;
  onSearch: (v: string) => void;
  onPage: (p: number) => void;
  onSuspend: (id: number) => void;
  onReactivate: (id: number) => void;
}

function statusBadge(shop: AdminShop) {
  if (!shop.is_installed) return <Badge tone="critical">Uninstalled</Badge>;
  if (!shop.is_active)    return <Badge tone="warning">Suspended</Badge>;
  return <Badge tone="success">Active</Badge>;
}

export function AdminShopsTable({
  shops, loading, error, actionId,
  page, lastPage, total, search,
  onSearch, onPage, onSuspend, onReactivate,
}: Props) {
  const rows = shops.map((shop) => [
    shop.id,
    shop.myshopify_domain,
    shop.name,
    shop.email ?? '—',
    statusBadge(shop),
    shop.currency ?? '—',
    shop.installed_at ? new Date(shop.installed_at).toLocaleDateString() : '—',
    <InlineStack gap="200" key={shop.id}>
      {shop.is_active ? (
        <Button
          size="slim"
          tone="critical"
          loading={actionId === shop.id}
          onClick={() => onSuspend(shop.id)}
          disabled={!shop.is_installed}
        >
          Suspend
        </Button>
      ) : (
        <Button
          size="slim"
          loading={actionId === shop.id}
          onClick={() => onReactivate(shop.id)}
        >
          Reactivate
        </Button>
      )}
    </InlineStack>,
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Shops ({total})</Text>
          <div style={{ width: 280 }}>
            <TextField
              label=""
              labelHidden
              placeholder="Search by domain, name, email…"
              value={search}
              onChange={onSearch}
              autoComplete="off"
            />
          </div>
        </InlineStack>

        {error && <Banner tone="critical">{error}</Banner>}

        {loading ? (
          <Box padding="800"><InlineStack align="center"><Spinner /></InlineStack></Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['ID', 'Domain', 'Name', 'Email', 'Status', 'Currency', 'Installed', 'Actions']}
              rows={rows}
            />
            {lastPage > 1 && (
              <InlineStack align="center">
                <Pagination
                  hasPrevious={page > 1}
                  onPrevious={() => onPage(page - 1)}
                  hasNext={page < lastPage}
                  onNext={() => onPage(page + 1)}
                />
              </InlineStack>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  );
}
